import { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import { getChatToolLoadingMessageDescriptor, type ChatToolLoadingKey } from 'tripPlanner/config/chatToolLoading'
import { EMPTY_SUMMARIES, type ChatLoadingStatus } from 'tripPlanner/assistant/runtime/chatStream'
import ChatLoadingAnimation from './ChatLoadingAnimation'
import { formatToolCallLabel } from './chatLoadingLabel'

interface Props {
  isUpdatingItinerary?: boolean
  activeToolLoadingKey?: ChatToolLoadingKey
  isOnline?: boolean
  reasoningSummaries?: ReadonlyArray<string>
  toolCallInfo?: { tool: string; args: Record<string, unknown> }
  loadingStatus?: ChatLoadingStatus
}

function useCyclingSummaries(summaries: ReadonlyArray<string>, intervalMs = 5000): string | undefined {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    setIndex(0)
    if (summaries.length <= 1) return
    const id = setInterval(() => {
      setIndex(prev => (prev + 1) % summaries.length)
    }, intervalMs)
    return () => clearInterval(id)
  }, [summaries, intervalMs])

  if (summaries.length === 0) return undefined
  return summaries[index % summaries.length]
}

function ChatLoadingIndicator({
  isUpdatingItinerary = false,
  activeToolLoadingKey,
  isOnline = true,
  reasoningSummaries = EMPTY_SUMMARIES,
  toolCallInfo,
  loadingStatus = 'idle',
}: Props) {
  const intl = useIntl()
  const currentSummary = useCyclingSummaries(reasoningSummaries)

  const toolCallLabel = useMemo(() => {
    if (!toolCallInfo) return undefined
    return formatToolCallLabel(toolCallInfo.tool, toolCallInfo.args, intl)
  }, [toolCallInfo, intl])

  const connectionStatusLabel = useMemo(() => {
    if (!isOnline) {
      return intl.formatMessage({
        id: 'p7Quu-YJBMFaMHUl1gYI',
        defaultMessage: "You're offline — will retry when connected",
        description: 'Loading indicator label when device has no network connection during chat streaming',
      })
    }
    if (loadingStatus === 'reconnecting') {
      return intl.formatMessage({
        id: 'JkIU3nFYM1ZEpdtx6OLg',
        defaultMessage: 'Reconnecting…',
        description: 'Loading indicator label while reconnecting an interrupted assistant chat stream',
      })
    }
    return undefined
  }, [isOnline, loadingStatus, intl])

  const toolLoadingLabel = useMemo(() => {
    if (!activeToolLoadingKey) return undefined
    return intl.formatMessage(
      getChatToolLoadingMessageDescriptor(activeToolLoadingKey),
    )
  }, [activeToolLoadingKey, intl])

  const reasoningFallbackLabel = useMemo(() => {
    return intl.formatMessage({
      id: 'lj2_OxUq7xoaw2FGp47h',
      defaultMessage: 'Thinking\u2026',
      description: 'Fallback loading label during AI reasoning when Groq summaries are unavailable in trip planner chat',
    })
  }, [intl])

  let itineraryLoadingLabel: string | undefined
  if (isUpdatingItinerary) { itineraryLoadingLabel = intl.formatMessage({
    id: 'Vyc6L4HykGywzgKBGcRY',
    defaultMessage: 'Updating your itinerary',
    description: 'Loading message while AI updates the trip itinerary',
  }) }
  const loadingLabel = connectionStatusLabel ?? itineraryLoadingLabel ?? toolLoadingLabel ?? toolCallLabel ?? currentSummary ?? reasoningFallbackLabel

  return (
    <Group direction="horizontal" gap={4} verticalAlign="center" horizontalAlign="start" role="status">
      <ChatLoadingAnimation imageId={isUpdatingItinerary ? '0jst326hyikkdg7pf9n' : '2cxvqkxsvq3fhnk5lsht'} />
      <BodyText variant="medium" weight="bold">{loadingLabel}</BodyText>
    </Group>
  )
}

export default ChatLoadingIndicator
